/**
 * AnniversaryGlowCelebration — Romantic premium anniversary invitation.
 *
 * Features: Elegant couple showcase, timeline of memories, love messages board,
 * romantic glow effects, champagne countdown, Marathi/English bilingual support.
 *
 * id: `anniversary-glow-celebration`
 */

import React, { useState, useMemo } from 'react';
import { registry } from '../registry';
import type { InvitationData, TemplateMetadata } from '../types';
import { HeroAnniversarySection } from './sections/HeroAnniversarySection';
import { CountdownAnniversarySection } from './sections/CountdownAnniversarySection';
import { TimelineMemoriesSection } from './sections/TimelineMemoriesSection';
import { LoveMessagesSection } from './sections/LoveMessagesSection';
import { VenueAnniversarySection } from './sections/VenueAnniversarySection';
import { RSVPAnniversarySection } from './sections/RSVPAnniversarySection';
import './anniversary-glow.css';

const META: TemplateMetadata = {
  id: 'anniversary-glow-celebration',
  name: 'Anniversary: Glow Celebration',
  occasionType: 'ANNIVERSARY',
  description: 'Romantic premium anniversary invitation with elegant couple showcase, memory timeline, love messages board, and champagne celebration theme.',
  isPremium: true,
  tags: ['anniversary', 'romantic', 'love', 'couple', 'premium', 'glow-effects', 'timeline'],
  colorScheme: {
    primary: '#D4AF37',
    accent: '#E91E63',
    bg: '#0F0F1E',
  },
};

export function AnniversaryGlowCelebration({ data }: { data: InvitationData }) {
  const customDataAny = (data.customData || {}) as any;
  const anniversaryData = customDataAny.anniversary || {};
  const [language, setLanguage] = useState<'en' | 'mr'>('en');
  const [loveMessagesCount, setLoveMessagesCount] = useState(0);

  const content = useMemo(() => {
    const partnerName1 = anniversaryData.partnerName1 || 'Partner One';
    const partnerName1Mr = anniversaryData.partnerName1Mr || partnerName1;
    const partnerName2 = anniversaryData.partnerName2 || 'Partner Two';
    const partnerName2Mr = anniversaryData.partnerName2Mr || partnerName2;
    const anniversaryDate = data.eventDate ? new Date(data.eventDate) : new Date();
    const venueName = data.eventVenue || 'Celebration Venue';
    const venueNameMr = anniversaryData.eventVenueMr || venueName;

    return {
      couple: {
        partner1: partnerName1,
        partner1Mr: partnerName1Mr,
        partner2: partnerName2,
        partner2Mr: partnerName2Mr,
        photo1: anniversaryData.photo1 || data.mediaUrls?.[0] || 'https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&w=500',
        photo2: anniversaryData.photo2 || data.mediaUrls?.[1] || 'https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&w=500',
        couplePhoto: anniversaryData.couplePhoto || data.mediaUrls?.[2] || 'https://images.unsplash.com/photo-1519225421980-715cb0215aed?auto=format&fit=crop&w=800',
        yearsTogather: anniversaryData.yearsTogather || 5,
      },
      event: {
        date: anniversaryDate,
        dateStr: anniversaryDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }),
        dateMr: anniversaryDate.toLocaleDateString('mr-IN', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }),
        time: data.eventTime || '6:00 PM',
        venue: venueName,
        venueMr: venueNameMr,
        message: anniversaryData.message || 'Join us as we celebrate love, commitment, and beautiful memories together.',
        messageMr: anniversaryData.messageMr || 'प्रेम, प्रतिबद्धता आणि सुंदर स्मृती साजरा करण्यासाठी आमच्याबरोबर सामील व्हा।',
      },
      invitationId: data.invitationId ?? data.id ?? null,
      isLive: data.status === 'PUBLISHED' || data.status === 'PROCESSING',
    };
  }, [data, anniversaryData]);

  return (
    <div className="anniversary-glow min-h-screen bg-gradient-to-b from-slate-900 via-rose-900 to-slate-900 text-gray-100 overflow-hidden">
      {/* Romantic background glow */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-rose-600/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-amber-600/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
      </div>

      {/* Language Toggle */}
      <div className="fixed top-4 right-4 z-50 flex gap-2">
        <button
          onClick={() => setLanguage('en')}
          className={`px-4 py-2 rounded-full font-bold transition-all ${
            language === 'en'
              ? 'bg-amber-500 text-white shadow-lg shadow-amber-500 glow-effect'
              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
        >
          EN
        </button>
        <button
          onClick={() => setLanguage('mr')}
          className={`px-4 py-2 rounded-full font-bold transition-all ${
            language === 'mr'
              ? 'bg-rose-500 text-white shadow-lg shadow-rose-500 glow-effect'
              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
        >
          मराठी
        </button>
      </div>

      {/* Main content */}
      <div className="relative z-10">
        <HeroAnniversarySection couple={content.couple} language={language} />
        <CountdownAnniversarySection anniversaryDate={content.event.date} language={language} />
        <TimelineMemoriesSection language={language} />
        <LoveMessagesSection invitationId={content.invitationId} isLive={content.isLive} language={language} onMessageAdded={() => setLoveMessagesCount(c => c + 1)} totalMessages={loveMessagesCount} />
        <VenueAnniversarySection venue={content.event} language={language} />
        <RSVPAnniversarySection invitationId={content.invitationId} isLive={content.isLive} language={language} couple={content.couple} />
      </div>
    </div>
  );
}

registry.register({ metadata: META, component: AnniversaryGlowCelebration });
